import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, WritableSignal, computed, inject, signal } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { EdsTheme, LatteTheme, NeoTheme } from '../models/theme.model';
import { PASS_ACCEPT_HEADER } from '../interceptors/accept-header.context';
import { loadStylesheet } from '../utils/load-stylesheet';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  httpClient = inject(HttpClient);
  private readonly BASE_PATH = '/ws/integrated/v1/customization/neo';
  public _theme: WritableSignal<NeoTheme | undefined> = signal(undefined);
  public theme = computed(() => this._theme());
  private _isLatteTheme = signal<boolean>(true);
  public isLatteTheme = computed(() => this._isLatteTheme());

  fetchTheme(): Observable<EdsTheme | LatteTheme> {
    return this.httpClient
      .get(`${this.BASE_PATH}/themes/css`, {
        context: new HttpContext().set(PASS_ACCEPT_HEADER, false)
      })
      .pipe(
        map((json: any): EdsTheme | LatteTheme => {
          const theme = this.isEdsTheme(json) ? new EdsTheme() : new LatteTheme();

          return Object.assign(theme, json);
        }),
        catchError(() => {
          return throwError(() => new Error('Failed to load theme from server'));
        })
      );
  }
  
  loadTheme() {
    this.fetchTheme().subscribe((t: LatteTheme | EdsTheme) => {
        this.setIsLatteTheme(t);
        this.updateCSSVars(t);
        const neoTheme = this.convertToNeoTheme(t);
        this.loadFont(neoTheme.fontFamily);
        this._theme.set(neoTheme);
      });
  }

  private setIsLatteTheme(t: LatteTheme | EdsTheme) {
    this._isLatteTheme.set(!this.isEdsTheme(t));
  }

  private loadFont(fontFamily?: string) {
    if (!fontFamily || fontFamily === 'Poppins') {
      return;
    }
    const family = encodeURIComponent(fontFamily).replace(/%20/g, '+');
    loadStylesheet(document, `https://fonts.googleapis.com/css2?family=${family}:wght@300;400;500;600;700;800&display=swap`);
  }

  updateFontFamily(fontFamily: string) {
    this.loadFont(fontFamily);
    document.documentElement.style.setProperty('--font-family', fontFamily);
    const elements = document.querySelectorAll('*');
    elements.forEach((element) => {
      if (element instanceof HTMLElement) {
        if(element.style){
          element.style.fontFamily = fontFamily;
        }
      }
    });
  }

  updateCSSVars(t: LatteTheme | EdsTheme) {
    const themeColors = this.getThemeCssVars(t);

    Object.entries(themeColors).forEach(([key, value]) => {
      if (value) {
        this.updateVarIfExists(key, value);
      }
    });
  }

  public isEdsTheme(obj: any): obj is EdsTheme {
    return obj && 'themeJson' in obj;
  }

  private isNeoTheme(t: LatteTheme | EdsTheme | NeoTheme): t is NeoTheme {
    return (
      'primary' in t ||
      'primaryDark' in t ||
      'primaryContainer' in t
    );
  }

  private withFallbackFonts(fontFamily?: string): string {
    return `${fontFamily ?? 'Poppins'}, 'Poppins Fallback', sans-serif`;
  }

  getThemeCssVars(t: LatteTheme | EdsTheme | NeoTheme): Record<string, string> {

    // Eds Theme
    if (this.isEdsTheme(t)) {
      const theme = t.themeJson;
      return {
        primary: theme?.primary ?? '',
        'primary-dark': theme?.primaryDark ?? '',
        'primary-container': theme?.primaryContainer ?? '',
        'primary-text-color': theme?.text ?? '#353535',
        'font-family': this.withFallbackFonts(theme?.fontFamily)
      };
    }

    // NeoTheme
    if (this.isNeoTheme(t)) {
      return {
        primary: t.primary ?? '',
        'primary-dark': t.primaryDark ?? '',
        'primary-container': t.primaryContainer ?? '',
        'primary-text-color': t.text ?? '#353535',
        'font-family': this.withFallbackFonts(t.fontFamily)
      };
    }

    // LatteTheme (fallback)
    return {
      primary: t.selectionColor ?? '',
      'primary-dark': t.actionInverseColor ?? '',
      'primary-container': t.actionColor ?? '',
      'primary-text-color': t.primaryTextColor ?? '#353535',
    };
  }

  convertToNeoTheme(t: LatteTheme | EdsTheme): NeoTheme {
    if (this.isEdsTheme(t)) {
      return {
        ...t.themeJson,
        hideCareerQuestionnaireLink: t.themeJson?.hideCareerQuestionnaireLink ?? false,
        hideContactQuestionnaireLink: t.themeJson?.hideContactQuestionnaireLink ?? false,
        showSearchMenuLink: t.themeJson?.showSearchMenuLink ?? true,
      };
    } else {
      return {
        primaryContainer: t.actionColor,
        primaryDark: t.actionInverseColor,
        primary: t.selectionColor,
        text: t.primaryTextColor,
        hideCareerQuestionnaireLink: t.hideCareerQuestionnaireLink,
        hideContactQuestionnaireLink: t.hideContactQuestionnaireLink,
        showSearchMenuLink: t.showSearchMenuLink,
      };
    }
  }

  updateVarIfExists(propertyName: string, value?: string) {
    if(value){
      document.documentElement.style.setProperty(
        `--${propertyName}`,
        value 
      );
    }
  }
  
}
